import styled from 'styled-components';
import type {
  ButtonVariation,
  DoubleBannerDescriptions,
  MediaFit,
} from '@amplience/content-types/typings/c-double-banner';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { FlexibleTextPartial } from '@storefront/lib-global/partials/flexibleText';
import { LinkPartial } from '@storefront/lib-global/partials/link';
import type { MediaPartialProps } from '@storefront/lib-global/partials/media';
import { MediaPartial } from '@storefront/lib-global/partials/media';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import { TypographyStyles, TypographyStylesType, colours, media, spacing } from '@storefront/lib-global/stylings';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import { BannerButtonStyles } from '../../utils';
import type { DoubleBannerProps } from './bannerType';

export type OverlappingBannerProps = DoubleBannerProps;

const S = {
  OverlappingBannerContainer: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing.S};
    padding: ${spacing.S} 0;

    @media ${media.greaterThan('lg')} {
      gap: 70px;
    }

    @media ${media.greaterThan('xl')} {
      padding: 70px 0;
    }
  `,
  OverlappingBannerMediasContainer: styled.div`
    padding-left: 24%;
    position: relative;
    width: 100%;

    @media ${media.greaterThan('lg')} {
      padding-left: 23%;
    }
  `,
  OverlappingBannerFrontMedia: styled.div<{ $mediaFit?: MediaFit }>`
    aspect-ratio: 1/1;
    left: -31%;
    max-width: 167px;
    position: absolute;
    top: 40%;
    width: 100%;

    & video {
      height: 100%;
      object-fit: ${({ $mediaFit }) => $mediaFit};
      position: absolute;
      width: 100%;
    }

    @media ${media.greaterThan('sm')} {
      max-width: 260px;
      top: 50%;
      transform: translateY(-50%);
    }

    @media ${media.greaterThan('lg')} {
      left: -30%;
      max-width: 454px;
      top: 50%;
      transform: translateY(-50%);
    }
  `,
  OverlappingBannerBackMedia: styled.div<{ $mediaFit?: MediaFit }>`
    aspect-ratio: 3/2;
    min-height: 372px;
    position: relative;
    width: 100%;

    & video {
      height: 100%;
      object-fit: ${({ $mediaFit }) => $mediaFit};
      position: absolute;
      width: 100%;
    }

    @media ${media.greaterThan('lg')} {
      margin: unset;
      min-height: 748px;
    }
  `,
  OverlappingBannerDetails: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing.S};
    justify-content: space-between;
    text-align: left;
    width: 100%;

    @media ${media.greaterThan('lg')} {
      flex-direction: row;
      margin: 0;
    }
  `,
  OverlappingBannerTitle: styled(FlexibleTextPartial)`
    margin: 0;
    width: fit-content;
  `,
  OverlappingBannerDescriptions: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing.S};
    overflow-wrap: break-word;
    width: 100%;

    @media ${media.greaterThan('lg')} {
      max-width: 435px;
    }
  `,
  OverlappingBannerDescription: styled(RichTextPartial)<{ $textColor?: string }>`
    color: ${({ $textColor }) => $textColor};

    & > p {
      margin: 0;
    }

    & a {
      color: ${({ $textColor }) => $textColor};
    }
  `,
  OverlappingBannerButtonsContainer: styled.ul`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${spacing.XS};
    margin: 0 auto;
    max-width: 1440px;
    padding: 0;
    width: 100%;

    & li,
    & a {
      max-width: 100%;
      width: 100%;
    }

    @media ${media.greaterThan('lg')} {
      flex-direction: row;
      justify-content: center;

      & li,
      & a {
        max-width: 340px;
      }
    }
  `,
  OverlappingBannerButton: styled(LinkPartial)<{ $variation?: ButtonVariation }>`
    ${({ $variation }) => BannerButtonStyles($variation)}
    max-width: 340px;
  `,
};

export function OverlappingBanner({
  bannerTitle,
  bannerDescriptions,
  bannerMedia,
  bannerButtons,
  onClick,
}: OverlappingBannerProps) {
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const currentMedia = isDesktop ? bannerMedia?.desktopMedia : bannerMedia?.mobileMedia;
  const [frontMedia, backMedia] = currentMedia ?? [];
  const [firstButton, ...restOfButtons] = bannerButtons ?? [];

  return (
    <S.OverlappingBannerContainer>
      <S.OverlappingBannerDetails>
        {!!bannerTitle?.text && (
          <S.OverlappingBannerTitle
            {...bannerTitle}
            text={getLocalizedValue(bannerTitle?.text)}
            defaultTextColor={colours.BLACK}
            defaultTextSize={TypographyStylesType.HEADINGS_XX_LARGE}
            $textSize={bannerTitle?.textSize}
          />
        )}
        <S.OverlappingBannerDescriptions>
          {(bannerDescriptions as DoubleBannerDescriptions[] | undefined)?.map((description, index) => (
            <S.OverlappingBannerDescription
              key={`${description.richText}-${index}`}
              text={getLocalizedValue(description.richText)}
              fontSize={TypographyStyles.Body.Medium.Regular}
              $textColor={description.textColor as string}
            />
          ))}
          <S.OverlappingBannerButton $variation={firstButton?.buttonVariation} {...firstButton?.link} onClick={onClick}>
            {getLocalizedValue(firstButton?.buttonText)}
          </S.OverlappingBannerButton>
        </S.OverlappingBannerDescriptions>
      </S.OverlappingBannerDetails>
      <S.OverlappingBannerMediasContainer>
        <S.OverlappingBannerBackMedia $mediaFit={backMedia?.mediaFit}>
          <MediaPartial
            {...(backMedia as MediaPartialProps)}
            height={100}
            width={100}
            isImageLayoutFill
            mediaFit={backMedia?.mediaFit}
            videoQuality={backMedia?.videoQuality}
            altText={backMedia?.altText}
          />
          <S.OverlappingBannerFrontMedia $mediaFit={frontMedia?.mediaFit}>
            <MediaPartial
              {...(frontMedia as MediaPartialProps)}
              height={100}
              width={100}
              isImageLayoutFill
              mediaFit={frontMedia?.mediaFit}
              videoQuality={frontMedia?.videoQuality}
              altText={frontMedia?.altText}
            />
          </S.OverlappingBannerFrontMedia>
        </S.OverlappingBannerBackMedia>
      </S.OverlappingBannerMediasContainer>
      {!!restOfButtons.length && (
        <S.OverlappingBannerButtonsContainer>
          {restOfButtons?.map((button, idx) => (
            <li key={`${button.buttonText}-${idx}`}>
              <S.OverlappingBannerButton $variation={button.buttonVariation} {...button.link} onClick={onClick}>
                {getLocalizedValue(button.buttonText)}
              </S.OverlappingBannerButton>
            </li>
          ))}
        </S.OverlappingBannerButtonsContainer>
      )}
    </S.OverlappingBannerContainer>
  );
}

export default OverlappingBanner;
